import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Receipt, Wallet, Users, Settings as SettingsIcon,
  LogOut, Menu, Zap, ChevronRight, Code2, Banknote, FileBarChart, BellRing,
  Landmark, WalletCards, FilePlus2, ClipboardCheck, ScrollText, Scale, PiggyBank, TrendingUp, Link2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABEL } from "@/lib/format";
import { ThemeToggle } from "@/components/ThemeToggle";
import { NotificationBell } from "@/components/NotificationBell";
import { TopBalance } from "@/components/TopBalance";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";

const OPS = [
  { section: "Overview", items: [{ to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true }] },
  { section: "Bank providers", items: [
    { to: "/app/bank-accounts", label: "Bank accounts", icon: Landmark },
    { to: "/app/bank-account-requests", label: "Review account requests", icon: ClipboardCheck },
    { to: "/app/wallets", label: "Wallets", icon: WalletCards },
    { to: "/app/wallet-requests", label: "Review wallet requests", icon: ClipboardCheck },
  ]},
  { section: "Transactions", items: [
    { to: "/app/transactions", label: "Deposit payments", icon: Receipt },
    { to: "/app/payment-links", label: "Payment links", icon: Link2 },
    { to: "/app/deposit-report", label: "Deposit report", icon: ScrollText },
    { to: "/app/withdraw-report", label: "Withdraw report", icon: Banknote },
    { to: "/app/payouts", label: "Pending withdrawals", icon: Banknote },
    { to: "/app/disputes", label: "Disputes", icon: Scale },
  ]},
  { section: "Finance", items: [
    { to: "/app/provider-balances", label: "Provider balances", icon: PiggyBank },
    { to: "/app/provider-payments", label: "Provider payments", icon: TrendingUp },
  ]},
];

const NAV = {
  super_admin: [
    ...OPS,
    { section: "Admin", items: [
      { to: "/app/users", label: "Users & Network", icon: Users },
      { to: "/app/settings", label: "Platform Settings", icon: SettingsIcon },
      { to: "/app/paytm", label: "Utsav Paytm", icon: Link2 },
      { to: "/app/setu", label: "Setu Links", icon: Link2 },
      { to: "/app/preferences", label: "Alerts", icon: BellRing },
    ]},
  ],
  admin: OPS,
  distributor: [
    { section: "Overview", items: [{ to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true }] },
    { section: "Bank providers", items: [
      { to: "/app/bank-accounts", label: "Bank accounts", icon: Landmark },
      { to: "/app/wallets", label: "Wallets", icon: WalletCards },
      { to: "/app/wallet-requests", label: "Wallet requests", icon: FilePlus2 },
    ]},
    { section: "Transactions", items: [
      { to: "/app/transactions", label: "Deposit payments", icon: Receipt },
      { to: "/app/payment-links", label: "Payment links", icon: Link2 },
      { to: "/app/deposit-report", label: "Deposit report", icon: ScrollText },
      { to: "/app/withdraw-report", label: "Withdraw report", icon: Banknote },
      { to: "/app/payouts", label: "Payouts", icon: Banknote },
      { to: "/app/disputes", label: "Disputes", icon: Scale },
    ]},
    { section: "Network", items: [
      { to: "/app/users", label: "My Merchants", icon: Users },
      { to: "/app/provider-balances", label: "Provider balances", icon: PiggyBank },
      { to: "/app/earnings", label: "Earnings", icon: Wallet },
      { to: "/app/statements", label: "Statements", icon: FileBarChart },
    ]},
  ],
  merchant: [
    { section: "Overview", items: [{ to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true }] },
    { section: "Bank accounts", items: [
      { to: "/app/bank-accounts", label: "Bank accounts", icon: Landmark },
      { to: "/app/bank-account-requests", label: "Add account request", icon: FilePlus2 },
      { to: "/app/wallets", label: "My wallet", icon: WalletCards },
      { to: "/app/wallet-requests", label: "Wallet requests", icon: FilePlus2 },
    ]},
    { section: "Transactions", items: [
      { to: "/app/transactions", label: "Collections", icon: Receipt },
      { to: "/app/payment-links", label: "Payment links", icon: Link2 },
      { to: "/app/deposit-report", label: "Deposit report", icon: ScrollText },
      { to: "/app/payouts", label: "Payouts", icon: Banknote },
      { to: "/app/disputes", label: "Raise dispute", icon: Scale },
    ]},
    { section: "Tools", items: [
      { to: "/app/earnings", label: "Settlements", icon: Wallet },
      { to: "/app/statements", label: "Statements", icon: FileBarChart },
      { to: "/app/developer", label: "Developer", icon: Code2 },
      { to: "/app/preferences", label: "Alerts", icon: BellRing },
    ]},
  ],
};

const SideNav = ({ groups, onNavigate }) => (
  <nav className="flex flex-col gap-4 px-3 pb-4">
    {groups.map((g) => (
      <div key={g.section} className="flex flex-col gap-1">
        <p className="px-3 pt-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70">{g.section}</p>
        {g.items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            onClick={onNavigate}
            data-testid={`nav-${it.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={({ isActive }) =>
              `group flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <it.icon className="h-4.5 w-4.5" />
                <span className="flex-1">{it.label}</span>
                {isActive && <ChevronRight className="h-4 w-4" />}
              </>
            )}
          </NavLink>
        ))}
      </div>
    ))}
  </nav>
);

const Brand = () => (
  <div className="flex items-center gap-2.5 px-5 py-5">
    <div className="h-9 w-9 rounded-xl bg-primary grid place-items-center shadow-lg shadow-primary/30">
      <Zap className="h-5 w-5 text-primary-foreground" fill="currentColor" />
    </div>
    <div>
      <p className="font-heading font-extrabold text-lg leading-none tracking-tight">WoltPe</p>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">UPI Collect</p>
    </div>
  </div>
);

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const groups = NAV[user.role] || [];
  const initials = (user.name || "U").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const Aside = ({ onNavigate }) => (
    <div className="flex h-full flex-col">
      <Brand />
      <div className="px-5 pb-4">
        <Badge variant="outline" className="border-primary/30 bg-primary/5 text-primary">
          {ROLE_LABEL[user.role]}
        </Badge>
      </div>
      <div className="flex-1 overflow-y-auto">
        <SideNav groups={groups} onNavigate={onNavigate} />
      </div>
      <div className="border-t p-3">
        <div className="flex items-center gap-3 rounded-xl px-2 py-2">
          <Avatar className="h-9 w-9"><AvatarFallback className="bg-primary/15 text-primary text-xs font-semibold">{initials}</AvatarFallback></Avatar>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{user.name}</p>
            <p className="truncate text-xs text-muted-foreground">{user.email}</p>
          </div>
        </div>
        <Button variant="ghost" className="mt-1 w-full justify-start text-muted-foreground" onClick={handleLogout} data-testid="logout-button">
          <LogOut className="mr-2 h-4 w-4" /> Sign out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background grain-overlay">
      <div className="flex">
        <aside className="hidden lg:flex w-64 shrink-0 h-screen sticky top-0 border-r bg-card/40">
          <Aside />
        </aside>

        <div className="flex-1 min-w-0">
          <header className="sticky top-0 z-30 flex items-center justify-between gap-4 border-b bg-card/60 glass px-4 md:px-8 py-3">
            <div className="flex items-center gap-3">
              <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="icon" className="lg:hidden" data-testid="mobile-menu-button">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-72 p-0">
                  <Aside onNavigate={() => setOpen(false)} />
                </SheetContent>
              </Sheet>
              <h1 className="font-heading text-base md:text-lg font-semibold">
                Welcome back, {user.name.split(" ")[0]}
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <TopBalance />
              <a href="/" target="_blank" rel="noreferrer">
                <Button variant="ghost" size="sm" className="hidden md:inline-flex text-muted-foreground">How it works</Button>
              </a>
              <NotificationBell />
              <ThemeToggle />
            </div>
          </header>
          <main className="p-4 md:p-8">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
